import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { X, Plus } from "lucide-react";
import { ValidationRuleDto } from "@/api/Validations";
import { Account } from "@/types/Account";

interface AccountSelectorProps {
  rule: ValidationRuleDto;
  ruleId: number;
  accounts: Account[];
  accountsLoading: boolean;
  onUpdateRule: (ruleId: number, updates: Partial<ValidationRuleDto>) => void;
}

// Data parsing performance logging
const logParsingPerformance = (input: string | null | undefined, output: string[], operation: string) => {
  const hadCorruption = input?.includes?.('object Object') || input?.includes?.('[[[');
  console.log('[ACCOUNT_PARSING_ANALYSIS]', {
    operation,
    inputLength: input?.length || 0,
    outputCount: output.length,
    hadCorruption,
    originalInput: hadCorruption ? input : 'CLEAN',
    cleanedCount: output.length,
    timestamp: new Date().toISOString()
  });
};

// Check if accounts string represents a comparison rule (has "a" and/or "b" properties)
const isComparisonRule = (accountsString: string | null | undefined): boolean => {
  if (!accountsString) return false;
  
  // Handle empty brackets or empty strings
  const trimmed = accountsString.trim();
  if (trimmed === '' || trimmed === '{}' || trimmed === '[]') return false;
  
  try {
    const parsed = JSON.parse(accountsString);
    
    let hasComparisonStructure = false;
    
    // Handle direct object format: {"a": [...], "b": [...]}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && ('a' in parsed || 'b' in parsed)) {
      hasComparisonStructure = true;
    }
    
    // Handle array format: [{"a": "...", "b": "..."}]
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && ('a' in parsed[0] || 'b' in parsed[0])) {
      hasComparisonStructure = true;
    }
    
    // Debug logging to track what's happening after save
    console.log('[COMPARISON_RULE_DETECTION]', {
      accountsString,
      parsed,
      isArray: Array.isArray(parsed),
      hasComparisonStructure,
      timestamp: new Date().toISOString()
    });
    
    return hasComparisonStructure;
  } catch (error) {
    console.warn('[COMPARISON_RULE_DETECTION] Parse failed:', accountsString, error);
    return false;
  }
};

// Parse comparison rule accounts (with "a" and "b" sets)
const parseComparisonAccounts = (accountsString: string | null | undefined): { a: string[], b: string[] } => {
  if (!accountsString) return { a: [], b: [] };
  
  try {
    const parsed = JSON.parse(accountsString);
    
    // Handle direct object format: {"a": [...], "b": [...]}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const aAccounts = Array.isArray(parsed.a) ? parsed.a : (parsed.a ? [parsed.a] : []);
      const bAccounts = Array.isArray(parsed.b) ? parsed.b : (parsed.b ? [parsed.b] : []);
      return { a: aAccounts, b: bAccounts };
    }
    
    // Handle array format: [{"a": "...", "b": "..."}]
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
      const obj = parsed[0];
      const aAccounts = Array.isArray(obj.a) ? obj.a : (obj.a ? [obj.a] : []);
      const bAccounts = Array.isArray(obj.b) ? obj.b : (obj.b ? [obj.b] : []);
      return { a: aAccounts, b: bAccounts };
    }
  } catch (error) {
    console.warn('Failed to parse comparison accounts:', accountsString, error);
  }
  
  return { a: [], b: [] };
};

// Format comparison accounts back to JSON string (using array format to match backend expectations)
const formatComparisonAccounts = (aAccounts: string[], bAccounts: string[]): string => {
  // For single account values, use string format; for multiple, use array format
  const aValue = aAccounts.length === 1 ? aAccounts[0] : aAccounts;
  const bValue = bAccounts.length === 1 ? bAccounts[0] : bAccounts;
  
  // Use array wrapper format to match backend: [{"a": "...", "b": "..."}]
  return JSON.stringify([{ a: aValue, b: bValue }]);
};

// Ensure comparison rule structure is maintained
const ensureComparisonStructure = (rule: ValidationRuleDto): string => {
  if (rule.ruleType === "Comparison") {
    // If it's a comparison rule but doesn't have proper structure, initialize it
    if (!isComparisonRule(rule.accounts)) {
      return formatComparisonAccounts([], []);
    }
  }
  return rule.accounts || '';
};

const parseAccountIds = (accountsString: string | null | undefined): string[] => {
  if (!accountsString) return [];
  
  let cleanString = accountsString.trim();
  
  // Handle empty brackets "{}" 
  if (cleanString === '' || cleanString === '{}') {
    return [];
  }
  
  // Handle corrupted nested brackets like "[[[[[[[object Object],20101002,10030002]]]]]]"
  // Remove all brackets and extract content
  cleanString = cleanString.replace(/\[+/g, '').replace(/\]+/g, '');
  
  // Split by comma and clean up each ID
  const accountIds = cleanString.split(',').map(id => id.trim()).filter(id => {
    // Filter out empty strings, {}, and [object Object]
    return id && 
           id !== '{}' && 
           id !== '[object Object]' && 
           !id.includes('object Object') &&
           !id.startsWith('[') &&
           !id.endsWith(']');
  });
  
  // Log the parsing performance and corruption detection
  logParsingPerformance(accountsString, accountIds, 'parseAccountIds');
  
  return accountIds;
};

const formatAccountsString = (accountIds: string[]): string => {
  return accountIds.join(', ');
};

const getSelectedAccounts = (accountsString: string | null | undefined, accounts: Account[]): Account[] => {
  const selectedIds = parseAccountIds(accountsString);
  return accounts.filter(account => 
    selectedIds.includes(account.accountId.toString()) || 
    selectedIds.includes(account.accountKey)
  );
};

// Get selected accounts for comparison rules
const getComparisonSelectedAccounts = (accountIds: string[], accounts: Account[]): Account[] => {
  return accounts.filter(account => 
    accountIds.includes(account.accountId.toString()) || 
    accountIds.includes(account.accountKey)
  );
};

interface AccountSectionProps {
  selectedAccounts: Account[];
  availableAccounts: Account[];
  accountsLoading: boolean;
  onAddAccount: (accountKey: string) => void;
  onRemoveAccount: (accountKey: string) => void;
  placeholder: string;
}

const AccountSection: React.FC<AccountSectionProps> = ({
  selectedAccounts,
  availableAccounts,
  accountsLoading,
  onAddAccount,
  onRemoveAccount,
  placeholder
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAccounts = availableAccounts.filter(account => 
    (account.accountKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
     account.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-3">
      {/* Selected Accounts Tags */}
      <div className="flex flex-wrap gap-2">
        {selectedAccounts.map((account) => (
          <Badge
            key={account.accountKey}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-1"
          >
            <span className="text-xs">{account.accountKey}</span>
            <span className="text-xs text-muted-foreground">({account.name})</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto p-0 ml-1 hover:bg-transparent"
              onClick={() => onRemoveAccount(account.accountKey)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        {selectedAccounts.length === 0 && (
          <span className="text-sm text-muted-foreground italic">
            None selected
          </span>
        )}
      </div>
      
      {/* Add Account Section */}
      <div className="space-y-2">
        <Input
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={accountsLoading}
        />
        
        {searchTerm && (
          <div className="border rounded-md max-h-48 overflow-auto">
            {accountsLoading ? (
              <div className="p-3 text-sm text-muted-foreground">Loading accounts...</div>
            ) : filteredAccounts.length > 0 ? (
              filteredAccounts.slice(0, 10).map((account) => (
                <div
                  key={account.accountKey}
                  className="flex items-center justify-between p-2 hover:bg-muted cursor-pointer"
                  onClick={() => {
                    onAddAccount(account.accountKey);
                    setSearchTerm("");
                  }}
                >
                  <div>
                    <div className="font-medium text-sm">{account.accountKey}</div>
                    <div className="text-xs text-muted-foreground">{account.name}</div>
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </div>
              ))
            ) : (
              <div className="p-3 text-sm text-muted-foreground">No accounts found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const AccountSelector: React.FC<AccountSelectorProps> = ({ 
  rule, 
  ruleId, 
  accounts, 
  accountsLoading, 
  onUpdateRule 
}) => {
  // Ensure comparison rules maintain proper structure
  React.useEffect(() => {
    if (rule.ruleType === "Comparison" && !isComparisonRule(rule.accounts)) {
      console.log('[ACCOUNT_SELECTOR] Initializing comparison structure for rule:', ruleId);
      onUpdateRule(ruleId, { accounts: formatComparisonAccounts([], []) });
    }
  }, [rule.ruleType, rule.accounts, ruleId, onUpdateRule]);

  const isComparison = rule.ruleType === "Comparison";
  const hasValidComparisonStructure = isComparison && isComparisonRule(rule.accounts);
  
  // Handle comparison rules
  if (isComparison) {
    // If it's a comparison rule but doesn't have valid structure, show a fallback
    if (!hasValidComparisonStructure) {
      return (
        <div className="p-4 border border-orange-200 bg-orange-50 rounded-md">
          <div className="text-sm text-orange-800">
            <strong>Initializing comparison rule structure...</strong>
          </div>
          <div className="text-xs text-orange-600 mt-1">
            This comparison rule is being set up with the proper account structure.
          </div>
        </div>
      );
    }
    const comparisonAccounts = parseComparisonAccounts(rule.accounts);
    const aAccounts = getComparisonSelectedAccounts(comparisonAccounts.a, accounts);
    const bAccounts = getComparisonSelectedAccounts(comparisonAccounts.b, accounts);
    const excludeAccounts = getSelectedAccounts(rule.excludeAccounts, accounts);
    
    const getAllSelectedAccountIds = () => [
      ...comparisonAccounts.a,
      ...comparisonAccounts.b,
      ...parseAccountIds(rule.excludeAccounts)
    ];
    
    const getAvailableAccounts = (currentlySelected: Account[]) => {
      const allSelectedIds = getAllSelectedAccountIds();
      return accounts.filter(account => 
        !allSelectedIds.includes(account.accountKey) &&
        !currentlySelected.some(selected => selected.accountKey === account.accountKey)
      );
    };

    const handleAddAccountToSet = (accountKey: string, set: 'a' | 'b') => {
      const current = parseComparisonAccounts(rule.accounts);
      if (set === 'a' && !current.a.includes(accountKey)) {
        const newAccounts = { ...current, a: [...current.a, accountKey] };
        onUpdateRule(ruleId, { accounts: formatComparisonAccounts(newAccounts.a, newAccounts.b) });
      } else if (set === 'b' && !current.b.includes(accountKey)) {
        const newAccounts = { ...current, b: [...current.b, accountKey] };
        onUpdateRule(ruleId, { accounts: formatComparisonAccounts(newAccounts.a, newAccounts.b) });
      }
    };

    const handleRemoveAccountFromSet = (accountKey: string, set: 'a' | 'b') => {
      const current = parseComparisonAccounts(rule.accounts);
      if (set === 'a') {
        const newAccounts = { ...current, a: current.a.filter(id => id !== accountKey) };
        onUpdateRule(ruleId, { accounts: formatComparisonAccounts(newAccounts.a, newAccounts.b) });
      } else if (set === 'b') {
        const newAccounts = { ...current, b: current.b.filter(id => id !== accountKey) };
        onUpdateRule(ruleId, { accounts: formatComparisonAccounts(newAccounts.a, newAccounts.b) });
      }
    };

    const handleAddExcludeAccount = (accountKey: string) => {
      const currentIds = parseAccountIds(rule.excludeAccounts);
      if (!currentIds.includes(accountKey)) {
        const newIds = [...currentIds, accountKey];
        onUpdateRule(ruleId, { excludeAccounts: formatAccountsString(newIds) });
      }
    };

    const handleRemoveExcludeAccount = (accountKey: string) => {
      const currentIds = parseAccountIds(rule.excludeAccounts);
      const newIds = currentIds.filter(id => id !== accountKey);
      onUpdateRule(ruleId, { excludeAccounts: formatAccountsString(newIds) });
    };

    return (
      <Tabs defaultValue="setA" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="setA" className="text-sm">
            Account Set A
            {aAccounts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {aAccounts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="setB" className="text-sm">
            Account Set B
            {bAccounts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {bAccounts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="exclude" className="text-sm">
            Exclude
            {excludeAccounts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {excludeAccounts.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="setA" className="mt-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Primary accounts to compare (Set A). These accounts will be compared against Set B accounts.
            </p>
            <AccountSection
              selectedAccounts={aAccounts}
              availableAccounts={getAvailableAccounts(aAccounts)}
              accountsLoading={accountsLoading}
              onAddAccount={(accountKey) => handleAddAccountToSet(accountKey, 'a')}
              onRemoveAccount={(accountKey) => handleRemoveAccountFromSet(accountKey, 'a')}
              placeholder="Search accounts for Set A..."
            />
          </div>
        </TabsContent>
        
        <TabsContent value="setB" className="mt-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Secondary accounts to compare against (Set B). Leave empty to compare Set A against all other accounts.
            </p>
            <AccountSection
              selectedAccounts={bAccounts}
              availableAccounts={getAvailableAccounts(bAccounts)}
              accountsLoading={accountsLoading}
              onAddAccount={(accountKey) => handleAddAccountToSet(accountKey, 'b')}
              onRemoveAccount={(accountKey) => handleRemoveAccountFromSet(accountKey, 'b')}
              placeholder="Search accounts for Set B..."
            />
          </div>
        </TabsContent>
        
        <TabsContent value="exclude" className="mt-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Accounts to exclude from this comparison rule entirely.
            </p>
            <AccountSection
              selectedAccounts={excludeAccounts}
              availableAccounts={getAvailableAccounts(excludeAccounts)}
              accountsLoading={accountsLoading}
              onAddAccount={handleAddExcludeAccount}
              onRemoveAccount={handleRemoveExcludeAccount}
              placeholder="Search accounts to exclude..."
            />
          </div>
        </TabsContent>
      </Tabs>
    );
  }

  // Handle regular include/exclude rules (existing logic)
  const includeAccounts = getSelectedAccounts(rule.accounts, accounts);
  const excludeAccounts = getSelectedAccounts(rule.excludeAccounts, accounts);
  
  // Get accounts that are not already selected in either include or exclude
  const getAvailableAccounts = (currentlySelected: Account[]) => {
    const allSelectedIds = [
      ...parseAccountIds(rule.accounts),
      ...parseAccountIds(rule.excludeAccounts)
    ];
    
    return accounts.filter(account => 
      !allSelectedIds.includes(account.accountKey) &&
      !currentlySelected.some(selected => selected.accountKey === account.accountKey)
    );
  };

  const handleAddIncludeAccount = (accountKey: string) => {
    const currentIds = parseAccountIds(rule.accounts);
    if (!currentIds.includes(accountKey)) {
      const newIds = [...currentIds, accountKey];
      onUpdateRule(ruleId, { accounts: formatAccountsString(newIds) });
    }
  };

  const handleRemoveIncludeAccount = (accountKey: string) => {
    const currentIds = parseAccountIds(rule.accounts);
    const newIds = currentIds.filter(id => id !== accountKey);
    onUpdateRule(ruleId, { accounts: formatAccountsString(newIds) });
  };

  const handleAddExcludeAccount = (accountKey: string) => {
    const currentIds = parseAccountIds(rule.excludeAccounts);
    if (!currentIds.includes(accountKey)) {
      const newIds = [...currentIds, accountKey];
      onUpdateRule(ruleId, { excludeAccounts: formatAccountsString(newIds) });
    }
  };

  const handleRemoveExcludeAccount = (accountKey: string) => {
    const currentIds = parseAccountIds(rule.excludeAccounts);
    const newIds = currentIds.filter(id => id !== accountKey);
    onUpdateRule(ruleId, { excludeAccounts: formatAccountsString(newIds) });
  };

  return (
    <Tabs defaultValue="include" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="include" className="text-sm">
          Include Accounts
          {includeAccounts.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {includeAccounts.length}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="exclude" className="text-sm">
          Exclude Accounts
          {excludeAccounts.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {excludeAccounts.length}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="include" className="mt-4">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Accounts to include in this validation rule. Leave empty to include all accounts.
          </p>
          <AccountSection
            selectedAccounts={includeAccounts}
            availableAccounts={getAvailableAccounts(includeAccounts)}
            accountsLoading={accountsLoading}
            onAddAccount={handleAddIncludeAccount}
            onRemoveAccount={handleRemoveIncludeAccount}
            placeholder="Search accounts to include..."
          />
        </div>
      </TabsContent>
      
      <TabsContent value="exclude" className="mt-4">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Accounts to exclude from this validation rule. These accounts will not be validated even if they match other criteria.
          </p>
          <AccountSection
            selectedAccounts={excludeAccounts}
            availableAccounts={getAvailableAccounts(excludeAccounts)}
            accountsLoading={accountsLoading}
            onAddAccount={handleAddExcludeAccount}
            onRemoveAccount={handleRemoveExcludeAccount}
            placeholder="Search accounts to exclude..."
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}; 